import { computed, effect, inject, ResourceRef } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import {
  COMPARTIMENTS_SRO,
  DateUtils,
  ElementType,
  EpargneUtils,
  ProduitDTO,
  Simulation,
  TypeProduit
} from "@mra/hlp9002";
import { tapResponse } from "@ngrx/operators";
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState
} from "@ngrx/signals";
import { map, of } from "rxjs";
import { PageStructureService } from "src/app/shared/services/page-service/page-structure.service";
import { ProduitService } from "src/app/shared/services/produit/produit.service";
import { LocalStorageService, StateKey } from "../shared/services/local-storage.service";
import { errorsStore } from "./errors.store";
import { UserState } from "./state";

function createDefaultState(): UserState {
  return {
    originalSimulation: null,
    pageStructure: null,
    produit: null,
    simulation: null,
    typeProduit: null,
    userInited: false
  };
}

export const userStore = signalStore(
  { providedIn: "root" },

  withState(createDefaultState()),

  withComputed((state) => ({
    codeProduit: computed(() => state.simulation()?.contrat?.codeProduit),
    nbMoisAvantRetraite: computed(
      () => DateUtils.calcNbMoisAvantRetraite(state.simulation()?.affilie.naissance.date) ?? 0
    ),
    isProduitPts: computed(() => state.simulation()?.typeProduit === TypeProduit.PTS),
    originalIban: computed(() => state.simulation()?.coordonneesBancaires?.[0]?.iban),
    montantSRO: computed(() =>
      EpargneUtils.cumulerMontantPoches(state.simulation()?.epargneAcquise, COMPARTIMENTS_SRO)
    ),
    clausesBeneficaire: computed(() => state.produit()?.clausesBeneficiaires),
    isEligibleExoFiscale: computed(() => state.produit()?.compartiments.some((comp) => comp.prestationDeductible)),
    epargneCumulee: computed(() => {
      const simu = state.simulation();
      if (!simu) return null;
      return simu?.typeProduit === TypeProduit.PTS
        ? EpargneUtils.cumulerPointsPoches(simu?.epargneAcquise)
        : (EpargneUtils.cumulerMontantPoches(simu?.epargneAcquise) ?? null);
    })
  })),

  withMethods((store, produitService = inject(ProduitService), errorStore = inject(errorsStore)) => ({
    retrieveState(savedState: UserState): void {
      patchState(store, () => savedState ?? createDefaultState());
    },

    saveSimulation(simulation: Simulation, isOriginal: boolean = false): void {
      patchState(store, () => {
        const res: UserState = {
          simulation
        };
        if (isOriginal) {
          res.originalSimulation = simulation;
        }
        return res;
      });
    },

    saveTypeProduit(typeProduit: TypeProduit): void {
      patchState(store, () => ({ typeProduit }));
    },

    saveUserInited(userInited: boolean): void {
      patchState(store, () => ({ userInited }));
    },

    unsetSimulation(): void {
      patchState(store, (state) => ({
        ...createDefaultState(),
        userInited: state.userInited,
        pageStructure: state.pageStructure,
        typeProduit: state.typeProduit
      }));
    },

    createProduitResource: (): ResourceRef<ProduitDTO> =>
      rxResource({
        request: store.produit,
        loader: (params) => {
          if (params.request) return of(params.request);
          return produitService.getCurrentProduitDetails(store.codeProduit()).pipe(
            tapResponse({
              next: (produit) => patchState(store, { produit }),
              error: (e) => errorStore.addError(e as Error, `Impossible d'obtenir les informations du produit`)
            })
          );
        }
      })
  })),

  withProps((store) => {
    const errorStore = inject(errorsStore);
    const pageStructureService = inject(PageStructureService);

    return {
      pageStructureResource: rxResource({
        request: computed(() => ({
          structure: store.pageStructure(),
          typeProduit: store.simulation()?.typeProduit
        })),
        loader: (params) => {
          if (!params.request.typeProduit || params.request.structure?.typeProduit === params.request.typeProduit) {
            return of(params.request.structure);
          }
          return pageStructureService.getPageStructure().pipe(
            tapResponse({
              next: (structure) => {
                const header = structure.elements.find((elem) => elem.code === ElementType.HEADER);
                const footer = structure.elements.find((elem) => elem.code === ElementType.FOOTER);
                const breadcrumbs = structure.elements.find((elem) => elem.code === ElementType.BREADCRUMB);
                patchState(store, {
                  pageStructure: {
                    header,
                    footer,
                    breadcrumbs,
                    typeProduit: params.request.typeProduit
                  }
                });
              },
              error: (e) => errorStore.addError(e as Error, `Impossible d'obtenir la structure de la page`)
            }),
            map(() => store.pageStructure())
          );
        }
      })
    };
  }),

  withHooks((store) => {
    const localStorageService = inject(LocalStorageService);

    return {
      onInit() {
        const state = localStorageService.getSavedState<UserState>(StateKey.USER);
        if (state) store.retrieveState(state);
        effect(() => {
          const state: UserState = getState(store);
          localStorageService.saveSelectedSimulation(state?.originalSimulation?.code);
          localStorageService.saveState(StateKey.USER, state);
        });
      }
    };
  })
);
